import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { CartLightbox } from "@/components/CartLightbox";
import { SchoolVerifyModal } from "@/components/SchoolVerifyModal";
import { productUrl } from "@/lib/productUrl";
import { Trash2, Sparkles, LogIn } from "lucide-react";

export default function Cart() {
  const { user, loading, refresh: refreshAuth } = useAuth();
  // The cart context now handles both guest (localStorage) and signed-in
  // (server) modes transparently, so we just read `cart` and don't touch
  // `api.cart()` here anymore.
  const { cart, refresh: refreshCart, removeItem, isGuest } = useCart();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [extras, setExtras] = useState({ garment_bag: false, donation: 0 });

  useEffect(() => { refreshCart?.(); }, [refreshCart]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("hb_cart_extras");
      if (raw) setExtras({ garment_bag: false, donation: 0, ...JSON.parse(raw) });
    } catch { /* ignore */ }
  }, [showCheckout]);

  if (loading) return <div className="h-[60vh]" />;
  if (cart.items.length === 0) return <Empty msg="Your bag is empty" cta="/shop" ctaLabel="Shop the collection" />;

  const garmentBagFee = extras.garment_bag ? 35 : 0;
  const donation = Number(extras.donation) || 0;
  const total = (cart.subtotal || 0) + garmentBagFee + donation;

  const startCheckout = () => {
    // 1) Guest — send them to sign-in with a returnTo hook so we come back
    //    to the exact cart they were on.
    if (isGuest) {
      navigate(`/auth?returnTo=${encodeURIComponent("/cart")}&reason=checkout`);
      return;
    }
    // 2) Signed in but school not verified — verify modal (existing flow)
    if (!user?.school_verified) { setVerifyOpen(true); return; }
    // 3) All checks passed — open payment
    setShowCheckout(true);
  };

  return (
    <div data-testid="cart-page" className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-12">
      <h1 className="font-display text-5xl sm:text-6xl mb-10">Your Bag</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {cart.items.map((it) => (
            <div key={it.product_id + (it.size || "") + (it.color || "")} data-testid={`cart-item-${it.product_id}`} className="flex gap-5 border-b border-border pb-6">
              <img src={it.image || it.product.images[0]} alt={it.product.name} className="w-24 h-32 object-cover object-top bg-blush" />
              <div className="flex-1">
                <Link to={productUrl(it.product, it.color)}><h3 className="font-display text-2xl">{it.product.name}</h3></Link>
                <p className="font-body text-xs text-muted-foreground mt-1">{it.color ? `${it.color} · ` : ""}Size {it.size || "OS"} · Qty {it.quantity}</p>
                <p className="font-body text-sm mt-2">${it.line_total}</p>
              </div>
              <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="bg-blush rounded-sm p-8 h-fit">
          <h3 className="font-display text-3xl mb-6">Summary</h3>
          <div className="flex justify-between font-body text-sm mb-2"><span>Subtotal</span><span>${cart.subtotal}</span></div>
          {extras.garment_bag && <div className="flex justify-between font-body text-sm mb-2"><span>Custom Garment Bag</span><span>${garmentBagFee}</span></div>}
          {donation > 0 && <div className="flex justify-between font-body text-sm mb-2"><span>School donation</span><span>${donation.toFixed(2)}</span></div>}
          <div className="flex justify-between font-body text-sm mb-6 text-muted-foreground"><span>Shipping</span><span>Calculated at pickup</span></div>
          <div className="flex justify-between font-display text-2xl border-t border-ink/10 pt-4 mb-6"><span>Total</span><span>${total.toFixed(2)}</span></div>

          {/* Guest notice — informative, non-blocking */}
          {isGuest && (
            <div data-testid="cart-guest-notice" className="mb-4 border border-ink/15 bg-ivory rounded-sm p-3.5 flex items-start gap-2.5">
              <LogIn className="w-4 h-4 text-rosemetal shrink-0 mt-0.5" strokeWidth={1.75} />
              <p className="font-body text-xs text-ink/70 leading-relaxed">
                Shopping as a guest — <span className="text-ink">we'll ask you to sign in or create an account at payment</span>. Your bag will be saved.
              </p>
            </div>
          )}

          {/* Signed-in but unverified — existing verify banner */}
          {!isGuest && !user?.school_verified && (
            <div data-testid="cart-verify-banner" className="mb-4 border border-rosemetal/40 bg-blush/40 rounded-sm p-4">
              <p className="font-label text-[9px] tracking-widest text-rosemetal mb-1">STUDENTS ONLY · REQUIRED FOR CHECKOUT</p>
              <p className="font-body text-sm text-ink mb-3">Verify your school email to check out — it's 15 seconds and unlocks locking + your first free try-on.</p>
              <button
                data-testid="cart-verify-cta"
                onClick={() => setVerifyOpen(true)}
                className="w-full bg-ink text-cream py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" /> VERIFY MY SCHOOL
              </button>
            </div>
          )}

          <button
            data-testid="checkout-btn"
            onClick={startCheckout}
            className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {isGuest ? "Continue to Checkout" : "Checkout"}
          </button>
          <p className="font-body text-[10px] text-muted-foreground mt-3 text-center">Purchasing permanently secures your locked dress.</p>
        </div>
      </div>
      <CartLightbox
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        onSuccess={refreshCart}
        cart={cart}
        firstName={(user?.name || "").split(" ")[0] || "there"}
        schoolName={user?.school_name}
        onSchoolVerifyRequired={() => { setShowCheckout(false); setVerifyOpen(true); }}
      />
      <SchoolVerifyModal
        open={verifyOpen}
        reason="checkout"
        onClose={() => { setVerifyOpen(false); refreshAuth?.(); }}
      />
    </div>
  );
}

function Empty({ msg, cta, ctaLabel }) {
  return (
    <div className="h-[60vh] flex flex-col items-center justify-center gap-6">
      <p className="font-display text-4xl text-muted-foreground">{msg}</p>
      <Link to={cta} className="bg-ink text-ivory px-8 py-3.5 font-label text-[10px] rounded-sm">{ctaLabel}</Link>
    </div>
  );
}
